import { useState, useEffect } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { usePagePermission } from "../../Hook";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { SlidersHorizontal, RotateCcw, CalendarDays, Boxes, Bike, Users, Wallet, FileText, Truck } from "lucide-react";
import { Spin, DatePicker, Drawer, Space, Button as AntdButton } from "antd";
import { SERVER_API } from "../../Constant";
import axios from "axios";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const toNumber = (value: any): number =>
  Number(String(value ?? "0").replace(/,/g, "")) || 0;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-LK", {
    style: "currency",
    currency: "LKR",
    minimumFractionDigits: 2,
  }).format(value);

export const DaySummary = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    "day_summary" as any,
    ["admin", "manager"],
  );

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<any>(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hasApplied, setHasApplied] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draftRange, setDraftRange] = useState<any>(null);

  const fetchSummary = async (start: string, end: string) => {
    if (!start || !end) {
      alert("Please select both start and end dates");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `${SERVER_API}/Reports/fetch_day_summary.php`,
        { params: { startDate: start, endDate: end } },
      );
      const res = response.data;
      if (res.success) {
        setSummary(res);
      } else {
        alert(res.msg || "Failed to fetch summary");
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const openDrawer = () => {
    setDraftRange(startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null);
    setDrawerOpen(true);
  };

  const applyFilters = () => {
    const s = draftRange?.[0] ? draftRange[0].format("YYYY-MM-DD") : "";
    const e = draftRange?.[1] ? draftRange[1].format("YYYY-MM-DD") : "";
    setStartDate(s);
    setEndDate(e);
    setHasApplied(true);
    setDrawerOpen(false);
    fetchSummary(s, e);
  };

  const resetFilters = () => {
    setDraftRange(null);
    setStartDate("");
    setEndDate("");
    setSummary(null);
    setHasApplied(false);
    setDrawerOpen(false);
  };

  const periodLabel = startDate && endDate ? `${startDate} to ${endDate}` : "";

  const modelByType = (summary?.modelBreakdown || []).reduce((acc: any, row: any) => {
    (acc[row.v_type] = acc[row.v_type] || []).push(row);
    return acc;
  }, {});

  const stockTotals = summary?.stock?.totals || {};
  const dealerTotals = (summary?.stockOverview || []).reduce(
    (acc: any, row: any) => ({
      start_stock: acc.start_stock + toNumber(row.start_stock),
      ordered_in: acc.ordered_in + toNumber(row.ordered_in),
      ordered_out: acc.ordered_out + toNumber(row.ordered_out),
      sold: acc.sold + toNumber(row.sold),
      returned: acc.returned + toNumber(row.returned),
      available_now: acc.available_now + toNumber(row.available_now),
      assigned: acc.assigned + toNumber(row.assigned),
    }),
    { start_stock: 0, ordered_in: 0, ordered_out: 0, sold: 0, returned: 0, available_now: 0, assigned: 0 },
  );

  const inquiryTotals = (summary?.inquiries || []).reduce(
    (acc: any, row: any) => ({
      count: acc.count + toNumber(row.cnt),
      invoice: acc.invoice + toNumber(row.invoice_amount),
      paid: acc.paid + toNumber(row.paid_amount),
    }),
    { count: 0, invoice: 0, paid: 0 },
  );

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  const sectionTitle = (icon: any, title: string) => (
    <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
      {icon}
      <h2 className="font-semibold text-gray-800">{title}</h2>
      {periodLabel && (
        <span className="ml-2 text-xs text-gray-400 font-normal">{periodLabel}</span>
      )}
    </div>
  );

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Day Summary</h1>
        <Button
          onClick={openDrawer}
          className="flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
        >
          <SlidersHorizontal className="w-4 h-4 text-white" />
          <span className="font-medium text-sm">Period</span>
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-24">
          <Spin size="large" />
        </div>
      )}

      {!loading && summary && (
        <div className="flex flex-col gap-6 px-6 pb-6">
          {/* ============ SECTION 1: Stock overview per dealer ============ */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {sectionTitle(<Boxes className="w-4 h-4 text-emerald-600" />, "Stock Overview by Dealer")}
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              <Table className="min-w-max text-sm">
                <TableHeader className="bg-gray-100/50">
                  <TableRow className="border-b">
                    <TableHead className="py-3 px-4 font-semibold text-gray-700">Dealer</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Start Stock</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Ordered In</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Ordered Out</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Sold</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Returned</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Available Now</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Assigned</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(summary.stockOverview || []).map((row: any) => (
                    <TableRow key={row.dealer_id} className="border-b last:border-0 hover:bg-gray-50">
                      <TableCell className="px-4 py-3 font-medium text-gray-800">{row.name}</TableCell>
                      <TableCell className="px-4 py-3 text-right">{toNumber(row.start_stock)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-emerald-600 font-medium">{toNumber(row.ordered_in)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-blue-600 font-medium">{toNumber(row.ordered_out)}</TableCell>
                      <TableCell className="px-4 py-3 text-right">{toNumber(row.sold)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-red-600 font-medium">{toNumber(row.returned)}</TableCell>
                      <TableCell className="px-4 py-3 text-right font-semibold">{toNumber(row.available_now)}</TableCell>
                      <TableCell className="px-4 py-3 text-right text-gray-400">{toNumber(row.assigned)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-gray-50 border-t-2 border-gray-200">
                    <TableCell className="px-4 py-3 font-bold text-gray-800">TOTAL</TableCell>
                    <TableCell className="px-4 py-3 text-right font-bold">{dealerTotals.start_stock}</TableCell>
                    <TableCell className="px-4 py-3 text-right font-bold text-emerald-600">{dealerTotals.ordered_in}</TableCell>
                    <TableCell className="px-4 py-3 text-right font-bold text-blue-600">{dealerTotals.ordered_out}</TableCell>
                    <TableCell className="px-4 py-3 text-right font-bold">{dealerTotals.sold}</TableCell>
                    <TableCell className="px-4 py-3 text-right font-bold text-red-600">{dealerTotals.returned}</TableCell>
                    <TableCell className="px-4 py-3 text-right font-bold">{dealerTotals.available_now}</TableCell>
                    <TableCell className="px-4 py-3 text-right font-bold text-gray-500">{dealerTotals.assigned}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </div>

          {/* ============ SECTION 2: Type & model breakdown ============ */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {sectionTitle(<Bike className="w-4 h-4 text-emerald-600" />, "Breakdown by Vehicle Type & Model")}
            <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
              {Object.entries(modelByType).map(([type, rows]: any) => (
                <div key={type} className="border border-gray-100 rounded-lg overflow-hidden">
                  <div className="bg-emerald-50 px-4 py-2.5 flex items-center justify-between">
                    <span className="font-semibold text-emerald-800">{type}</span>
                    <span className="text-xs text-emerald-600">
                      {rows.reduce((a: number, r: any) => a + toNumber(r.total), 0)} total
                    </span>
                  </div>
                  <Table className="min-w-max w-full text-sm">
                    <TableHeader>
                      <TableRow className="border-b">
                        <TableHead className="py-2 px-4 font-semibold text-gray-700">Model</TableHead>
                        <TableHead className="py-2 px-4 font-semibold text-gray-700 text-right">Total</TableHead>
                        <TableHead className="py-2 px-4 font-semibold text-gray-700 text-right">Available</TableHead>
                        <TableHead className="py-2 px-4 font-semibold text-gray-700 text-right">Sold</TableHead>
                        <TableHead className="py-2 px-4 font-semibold text-gray-700 text-right">Returned</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row: any, i: number) => (
                        <TableRow key={i} className="border-b last:border-0 hover:bg-gray-50">
                          <TableCell className="px-4 py-2 text-gray-700">{row.v_modal}</TableCell>
                          <TableCell className="px-4 py-2 text-right">{toNumber(row.total)}</TableCell>
                          <TableCell className="px-4 py-2 text-right">{toNumber(row.available)}</TableCell>
                          <TableCell className="px-4 py-2 text-right text-emerald-600 font-medium">{toNumber(row.sold)}</TableCell>
                          <TableCell className="px-4 py-2 text-right text-red-600 font-medium">{toNumber(row.returned)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          </div>

          {/* ============ SECTION 3: Sales persons ============ */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {sectionTitle(<Users className="w-4 h-4 text-emerald-600" />, "Sales Person Performance")}
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              <Table className="min-w-max text-sm">
                <TableHeader className="bg-gray-100/50">
                  <TableRow className="border-b">
                    <TableHead className="py-3 px-4 font-semibold text-gray-700">Sales Person</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Deals Closed</TableHead>
                    <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(summary.salesPersons || []).map((row: any, i: number) => (
                    <TableRow key={i} className="border-b last:border-0 hover:bg-gray-50">
                      <TableCell className="px-4 py-3 font-medium text-gray-800">{row.name}</TableCell>
                      <TableCell className="px-4 py-3 text-right">{toNumber(row.deals)}</TableCell>
                      <TableCell className="px-4 py-3 text-right font-medium">
                        {formatCurrency(toNumber(row.amount))}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!summary.salesPersons || summary.salesPersons.length === 0) && (
                    <TableRow>
                      <TableCell className="px-4 py-6 text-center text-gray-400" colSpan={3}>
                        No sales recorded in period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* ============ SECTION 4: Money received / paid ============ */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {sectionTitle(<Wallet className="w-4 h-4 text-emerald-600" />, "Money Received / Paid")}
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-emerald-600 rounded-xl p-5 text-white">
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">DIRECT RECEIVED</p>
                <p className="text-xl font-bold">{formatCurrency(toNumber(summary.money?.directReceived?.amount))}</p>
                <p className="text-xs opacity-80 mt-1">{toNumber(summary.money?.directReceived?.count)} payments</p>
              </div>
              <div className="bg-amber-500 rounded-xl p-5 text-white">
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">DEALER PAYMENTS</p>
                <p className="text-xl font-bold">{formatCurrency(toNumber(summary.money?.dealerReceived?.amount))}</p>
                <p className="text-xs opacity-80 mt-1">{toNumber(summary.money?.dealerReceived?.count)} payments</p>
              </div>
              <div className="bg-blue-600 rounded-xl p-5 text-white">
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">DPMC PAID (REMITTANCE)</p>
                <p className="text-xl font-bold">{formatCurrency(toNumber(summary.money?.remittancePaid?.amount))}</p>
                <p className="text-xs opacity-80 mt-1">{toNumber(summary.money?.remittancePaid?.count)} transfers</p>
              </div>
              <div
                className={`rounded-xl p-5 text-white ${
                  toNumber(summary.money?.net) < 0 ? "bg-red-600" : "bg-emerald-700"
                }`}
              >
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">NET BALANCE</p>
                <p className="text-xl font-bold">{formatCurrency(toNumber(summary.money?.net))}</p>
                <p className="text-xs opacity-80 mt-1">Received − Paid</p>
              </div>
            </div>
          </div>

          {/* ============ SECTION 5: Inquiries with payment ============ */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {sectionTitle(<FileText className="w-4 h-4 text-emerald-600" />, "Inquiry Details (with Payment)")}
            <div className="p-5">
              <div className="flex flex-wrap gap-12 mb-4">
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">TOTAL INQUIRIES</p>
                  <p className="text-xl font-bold text-gray-800">{inquiryTotals.count}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">INVOICED</p>
                  <p className="text-xl font-bold text-gray-800">{formatCurrency(inquiryTotals.invoice)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">PAID AGAINST</p>
                  <p className="text-xl font-bold text-emerald-600">{formatCurrency(inquiryTotals.paid)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">OUTSTANDING</p>
                  <p className="text-xl font-bold text-red-600">
                    {formatCurrency(Math.max(0, inquiryTotals.invoice - inquiryTotals.paid))}
                  </p>
                </div>
              </div>
              <div className="overflow-auto max-h-[calc(100vh-320px)]">
                <Table className="min-w-max text-sm">
                  <TableHeader className="bg-gray-100/50">
                    <TableRow className="border-b">
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Status</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Count</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Invoiced</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Paid</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Outstanding</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(summary.inquiries || []).map((row: any, i: number) => (
                      <TableRow key={i} className="border-b last:border-0 hover:bg-gray-50">
                        <TableCell className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded text-[11px] font-medium ${
                              row.status === "Paid"
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                                : "bg-amber-50 text-amber-700 border border-amber-100"
                            }`}
                          >
                            {row.status}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right font-medium">{toNumber(row.cnt)}</TableCell>
                        <TableCell className="px-4 py-3 text-right">{formatCurrency(toNumber(row.invoice_amount))}</TableCell>
                        <TableCell className="px-4 py-3 text-right text-emerald-600 font-medium">
                          {formatCurrency(toNumber(row.paid_amount))}
                        </TableCell>
                        <TableCell className="px-4 py-3 text-right">
                          <span className={toNumber(row.invoice_amount) - toNumber(row.paid_amount) > 0 ? "text-red-600 font-medium" : "text-emerald-600 font-medium"}>
                            {formatCurrency(Math.max(0, toNumber(row.invoice_amount) - toNumber(row.paid_amount)))}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!summary.inquiries || summary.inquiries.length === 0) && (
                      <TableRow>
                        <TableCell className="px-4 py-6 text-center text-gray-400" colSpan={5}>
                          No inquiries in period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* ============ SECTION 6: Vehicle stock details ============ */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {sectionTitle(<Truck className="w-4 h-4 text-emerald-600" />, "Vehicle Stock Details (Onboarding)")}
            <div className="p-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-5">
                <div className="bg-slate-800 rounded-xl p-5 text-white">
                  <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">ONBOARDED</p>
                  <p className="text-xl font-bold">{toNumber(stockTotals.onboarded)}</p>
                  <p className="text-xs opacity-80 mt-1">added in period</p>
                </div>
                <div className="bg-emerald-600 rounded-xl p-5 text-white">
                  <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">AVAILABLE NOW</p>
                  <p className="text-xl font-bold">{toNumber(stockTotals.available_now)}</p>
                  <p className="text-xs opacity-80 mt-1">whole fleet</p>
                </div>
                <div className="bg-blue-600 rounded-xl p-5 text-white">
                  <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">SOLD</p>
                  <p className="text-xl font-bold">{toNumber(stockTotals.sold_period)}</p>
                  <p className="text-xs opacity-80 mt-1">in period</p>
                </div>
                <div className="bg-red-600 rounded-xl p-5 text-white">
                  <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">RETURNED</p>
                  <p className="text-xl font-bold">{toNumber(stockTotals.returned_period)}</p>
                  <p className="text-xs opacity-80 mt-1">in period</p>
                </div>
                <div className="bg-amber-500 rounded-xl p-5 text-white">
                  <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">STOCK TRANSFERS</p>
                  <p className="text-xl font-bold">{toNumber(stockTotals.transfers)}</p>
                  <p className="text-xs opacity-80 mt-1">in period</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="border border-gray-100 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 font-semibold text-gray-700 text-sm">By Type</div>
                  <Table className="min-w-max w-full text-sm">
                    <TableHeader>
                      <TableRow className="border-b">
                        <TableHead className="py-2 px-4 font-semibold text-gray-700">Type</TableHead>
                        <TableHead className="py-2 px-4 font-semibold text-gray-700 text-right">Onboarded</TableHead>
                        <TableHead className="py-2 px-4 font-semibold text-gray-700 text-right">Available</TableHead>
                        <TableHead className="py-2 px-4 font-semibold text-gray-700 text-right">Sold</TableHead>
                        <TableHead className="py-2 px-4 font-semibold text-gray-700 text-right">Returned</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(summary.stock?.byType || []).map((row: any, i: number) => (
                        <TableRow key={i} className="border-b last:border-0 hover:bg-gray-50">
                          <TableCell className="px-4 py-2 font-medium">{row.v_type}</TableCell>
                          <TableCell className="px-4 py-2 text-right">{toNumber(row.onboarded)}</TableCell>
                          <TableCell className="px-4 py-2 text-right">{toNumber(row.available_now)}</TableCell>
                          <TableCell className="px-4 py-2 text-right text-emerald-600 font-medium">{toNumber(row.sold_period)}</TableCell>
                          <TableCell className="px-4 py-2 text-right text-red-600 font-medium">{toNumber(row.returned_period)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="border border-gray-100 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 font-semibold text-gray-700 text-sm">Onboarded by Dealer</div>
                  <div className="flex flex-wrap gap-2 p-4">
                    {(summary.stock?.onboardedByDealer || []).map((row: any, i: number) => (
                      <span key={i} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs">
                        <span className="font-semibold">{row.name}</span>
                        <span className="ml-1.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold text-[10px]">
                          {toNumber(row.cnt)}
                        </span>
                      </span>
                    ))}
                    {(!summary.stock?.onboardedByDealer || summary.stock.onboardedByDealer.length === 0) && (
                      <p className="text-sm text-gray-400">No vehicles onboarded in period</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!loading && !summary && hasApplied && (
        <div className="px-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No summary data for the applied period.</p>
          </div>
        </div>
      )}
      {!loading && !summary && !hasApplied && (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <CalendarDays className="w-12 h-12 mb-4 opacity-40" />
          <p>Open Period and pick a date range to view the full business summary.</p>
        </div>
      )}

      <Drawer
        title="Day Summary Period"
        placement="right"
        width={400}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        footer={
          <div className="flex justify-end gap-3">
            <AntdButton onClick={resetFilters} className="flex items-center gap-2 border-gray-200">
              <RotateCcw className="w-4 h-4" />
              Reset
            </AntdButton>
            <AntdButton type="primary" onClick={applyFilters} className="bg-emerald-600 hover:bg-emerald-700">
              Apply
            </AntdButton>
          </div>
        }
      >
        <Space direction="vertical" size="large" className="w-full">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
              Date Range
            </label>
            <RangePicker
              value={draftRange}
              onChange={(dates) => setDraftRange(dates)}
              className="w-full"
            />
          </div>
        </Space>
      </Drawer>
    </div>
  );
};